<script setup lang="ts">
import DrawerItem from "./DrawerItem.vue";
import ItemsHeader from "./ItemsHeader.vue";
import { ItemObject, MonitoringObject } from "./use-drawer-data";

export type ItemCollections = { title: string; collection: ItemObject[] };

const props = defineProps({
	collection: { type: Array<ItemObject>, required: true },
	header: Boolean,
});
const emit = defineEmits(["select"]);

const { t } = useI18n();

const vModel = useVModel(props, "collection");

function select(item: ItemObject) {
	emit("select", { id: item.id, type: item.object });
}

const { list, containerProps, wrapperProps } = useVirtualList(vModel, {
	itemHeight: 52,
	overscan: 10,
});

function getTitle(object: MonitoringObject) {
	switch (object) {
		case "site":
			return t("object.project", 2);
		case "station":
			return t("object.station", 2);
		case "space":
			return t("object.space", 2);
		case "report":
			return t("object.report", 2);
	}
}

const lists = computed<ItemCollections[]>(() => {
	const map = new Map<MonitoringObject, ItemObject[]>();
	let tempArray: ItemObject[] = [];

	list.value.map((c) => {
		switch (c.data.object) {
			case "site":
				tempArray = map.get("site") || [];
				tempArray.push(c.data);
				map.set("site", tempArray);
				break;
			case "station":
				tempArray = map.get("station") || [];
				tempArray.push(c.data);
				map.set("station", tempArray);
				break;
			case "space":
				tempArray = map.get("space") || [];
				tempArray.push(c.data);
				map.set("space", tempArray);
				break;
			case "report":
				tempArray = map.get("report") || [];
				tempArray.push(c.data);
				map.set("report", tempArray);
		}
	});

	const collections: ItemCollections[] = [];
	for (const key of map.keys()) {
		const title = getTitle(key);
		const collection = map.get(key) || [];
		collections.push({ title, collection });
	}

	return collections;
});

const variableHeight = computed(() => {
	return props.header ? 26 * lists.value.length : 0;
});

const updatedWrapper = computed(() => ({
	style: {
		...wrapperProps.value.style,
		height: parseInt(wrapperProps.value.style.height) + variableHeight.value + "px",
	},
}));
</script>

<template>
	<div
		class="flex flex-col gap-2 overflow-auto pb-2"
		:style="{ 'scrollbar-width': 'none' }"
		v-bind="containerProps"
	>
		<div class="relative flex flex-col gap-1" v-bind="updatedWrapper">
			<template v-for="objectList of lists">
				<ItemsHeader
					v-if="header && objectList.collection.length > 0 && lists.length > 1"
					:key="objectList.title"
					:title="objectList.title"
				/>
				<DrawerItem
					v-for="item of objectList.collection"
					:key="item.id"
					:item="item"
					@select="select"
				/>
			</template>
		</div>
	</div>
</template>
